// Lógica de dominio de DetalleObra: formatos es-AR, período habilitado
// (espejo del backend), procedencia de los datos proyectados en la lápida,
// derivación del ciclo de vida (stepper) por tipo y armado del hilo de
// Actividad. El estado YA viene derivado del backend (RdtEstadoDerivado),
// así que acá no se recalcula "Saltó"/"Sin salto"/etc., solo se arma la
// UI a partir de esos datos. Sin JSX: vive acá para que estado-chip.tsx,
// ficha.tsx y los demás componentes lo importen sin duplicar criterio.
import type {
  Adjunto,
  CampoEvento,
  ComentarioRdt,
  Interlocutor,
  RdtChequeo,
  RdtDetalle,
  RdtHito,
  RdtHitoDetalle,
  RdtMonto,
  RdtReferencia,
  RdtResumen,
  ResultadoSeguimiento,
  Seguimiento,
  TipoAdjunto,
  TipoRdt,
} from '@/types/api';

export const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

export const fmtMonto = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' });
export const fmtPct = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMillones = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fmtEntero = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 });

/** Monto abreviado es-AR para espacios chicos (header de obra, card de RDT):
 *  ≥ $ 1.000.000 se abrevia a millones con 1 decimal fijo ("$ 1.760,8 M"),
 *  si no, el monto completo con separador de miles y sin decimales
 *  ("$ 850"). Antes cada lugar lo formateaba a mano (header con fmtMillones
 *  suelto, sin decimal fijo → "$ 1 M" en vez de "$ 1,0 M"); ahora es un
 *  único criterio que reusan header-obra.tsx y rdt-list-card.tsx (fix
 *  cinturón de card desbordada, QA en vivo Juan 2026-09-14, obra 1426: el
 *  monto completo de una Definitiva empujaba el contador de comentarios
 *  fuera del borde de la card). El monto completo (fmtMonto) sigue
 *  mostrándose en la ficha, el hilo de Actividad y como tooltip acá. */
export function fmtMontoCorto(monto: number): string {
  const signo = monto < 0 ? '-' : '';
  const abs = Math.abs(monto);
  if (abs >= 1_000_000) return `${signo}$ ${fmtMillones.format(abs / 1e6)} M`;
  return `${signo}$ ${fmtEntero.format(abs)}`;
}

/** 'YYYY-MM' -> 'may-26'. */
export function perLabel(periodo: string): string {
  const [y, m] = periodo.split('-').map(Number);
  return `${MESES[m - 1]}-${String(y).slice(2)}`;
}

/** 'YYYY-MM-DD[...]' -> 'dd/mm/yyyy'. Evita new Date() (parseo UTC) — son
 *  strings ya en hora AR (pool con timezone -03:00), se parte a mano. */
export function fFecha(iso: string | null): string | null {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

/** 'YYYY-MM-DD[...]' -> 'dd/mm/yy'. */
export function fFechaCorta(iso: string | null): string | null {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y.slice(2)}`;
}

/** 'YYYY-MM-DD HH:MM:SS' -> 'dd/mm/yy HH:MM'. */
export function fHora(ts: string): string {
  const [f, h] = ts.split(' ');
  return `${fFechaCorta(f)}${h ? ' ' + h.slice(0, 5) : ''}`;
}

export function hoyIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Aviso de fecha futura (punto 18, extendido en la 2ª validación — punto 8):
 *  se avisa inline, nunca bloquea el submit — la app permite cargar fechas
 *  futuras (Juan cargó un chequeo con fecha de mañana en la rede 30/1480 y
 *  el form de chequeo no avisó nada). Único criterio compartido por todos
 *  los forms de paso con campo fecha: no reimplementar por archivo. */
export function esFechaFutura(fecha: string): boolean {
  return Boolean(fecha) && fecha > hoyIso();
}

// Espejo MANUAL de backend/src/domain/rdt-nro.ts (decisión de Juan
// 2026-09-14, caso real 1426·04 — ver el comentario del backend para el
// porqué): un entero puro se acolchona a 2 dígitos («4» -> «04»); un nro con
// sufijo de texto solo se recorta (trim + colapsar espacios internos). Se
// usa para el toast de "creada" cuando la respuesta no trae el `nro`
// guardado, y para no mandarle al backend espacios de más.
export function normalizarNro(raw: string): string {
  const limpio = raw.trim().replace(/\s+/g, ' ');
  return /^\d+$/.test(limpio) ? String(parseInt(limpio, 10)).padStart(2, '0') : limpio;
}

/** Período actual 'YYYY-MM' (calendario, sin regla de día D — uso genérico). */
export function periodoActual(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Iniciales de respaldo del avatar (1ª letra + última palabra) — mismo
 *  criterio que components/admin/user-avatar.tsx, copia local porque ese
 *  componente está atado a userId/fotoVersion y acá el shape es UsuarioMini. */
export function iniciales(nombre: string): string {
  const partes = nombre.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0]!.charAt(0).toUpperCase();
  return (partes[0]!.charAt(0) + partes[partes.length - 1]!.charAt(0)).toUpperCase();
}

export function hitoAplicable<H extends RdtHito>(hitos: H[], codigo: string): H | null {
  return hitos.find(h => h.codigo === codigo) ?? null;
}

/** El hito de entrega ELEGIDO (obra o cliente), tenga fecha o no todavía. */
export function entregaElegida<H extends RdtHito>(hitos: H[]): H | null {
  return hitos.find(h => h.codigo === 'entregado_obra' || h.codigo === 'entregado_cliente') ?? null;
}

/** El hito de entrega ya COMPLETADO (con fecha). */
export function entregaCompletada<H extends RdtHito>(hitos: H[]): H | null {
  const h = entregaElegida(hitos);
  return h && h.fecha ? h : null;
}

/** El hito COMPLETADO con mayor `orden` del catálogo — el más avanzado de la
 *  secuencia canónica, target de "Deshacer" (punto 11a v0.9.0): deshacer
 *  siempre ataca el último, nunca uno intermedio (orden inverso estricto,
 *  el backend lo vuelve a validar). */
export function ultimoHitoCompletado<H extends RdtHito>(hitos: H[]): H | null {
  const completados = hitos.filter(h => h.fecha !== null);
  if (completados.length === 0) return null;
  return completados.reduce((max, h) => (h.orden > max.orden ? h : max));
}

// ---------------------------------------------------------------------------
// Período mostrado en la card / ficha: el del salto si ya saltó, si no el
// aproximado de la entrega al cliente (migradas), si no "sin período".
// Pensado para Provisorias (el período de una Definitiva sale del monto
// vigente — ver procedenciaMonto() más abajo, cada consumidor lo resuelve
// por tipo antes de llamar a este helper).
// ---------------------------------------------------------------------------

interface ConSaltoYHitos {
  salto: { periodo: string } | null;
  hitos: RdtHito[];
}

export function periodoRdt(r: ConSaltoYHitos): string | null {
  if (r.salto) return perLabel(r.salto.periodo);
  const ec = hitoAplicable(r.hitos, 'entregado_cliente');
  if (ec?.fecha) {
    // migradas: período aproximado desde la fecha de índices histórica (la
    // entrega solía ir ~2 meses después del período que la disparó).
    let [y, m] = ec.fecha.slice(0, 10).split('-').map(Number);
    m -= 2;
    if (m < 0) {
      m += 12;
      y -= 1;
    }
    return `${MESES[m]}-${String(y).slice(2)}`;
  }
  return null;
}

/** Último chequeo registrado — el array ya viene ordenado ASC por período
 *  desde la API (ORDER BY periodo), así que el último elemento es el mayor. */
export function ultimoChequeo(r: { chequeos: RdtChequeo[] }): RdtChequeo | null {
  return r.chequeos.length ? r.chequeos[r.chequeos.length - 1]! : null;
}

// ---------------------------------------------------------------------------
// Período habilitado — ESPEJO MANUAL de backend/src/domain/periodos.ts.
// Si cambia la regla de allá (día D, offset AR), cambiar acá también.
// ---------------------------------------------------------------------------

export const DIA_D = 20;

const AR_OFFSET_MS = 3 * 60 * 60 * 1000;

/** Último período habilitado ('YYYY-MM') al momento `ahora`. */
export function periodoHabilitado(ahora: Date = new Date(), diaD: number = DIA_D): string {
  const ar = new Date(ahora.getTime() - AR_OFFSET_MS);
  const retro = ar.getUTCDate() >= diaD ? 1 : 2;
  const meses = ar.getUTCFullYear() * 12 + ar.getUTCMonth() - retro;
  return `${Math.floor(meses / 12)}-${String((meses % 12) + 1).padStart(2, '0')}`;
}

/** Período siguiente a uno dado ('2026-12' → '2027-01'). */
export function periodoSiguiente(periodo: string): string {
  const [y, m] = periodo.split('-').map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
}

/** Suma (o resta, con delta negativo) meses a un período — solo para armar
 *  el rango de backfill de opcionesPeriodo(); periodoSiguiente() de arriba
 *  se deja intacto como espejo textual 1:1 del backend. */
function sumarMeses(periodo: string, delta: number): string {
  const [y, m] = periodo.split('-').map(Number);
  const total = y * 12 + (m - 1) + delta;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

/** Últimos `cantidad` períodos hasta (e incluyendo) `hasta`, ascendente —
 *  movido acá desde paso-forms.tsx (Juan + panel, 2026-09-15): lo usa
 *  también MontoEditForm en actividad.tsx para el período del select de
 *  corrección de "Monto informado" (antes un <input type="month">, único
 *  widget de período distinto al que crea el paso — Nielsen #4). */
export function ultimosPeriodos(hasta: string, cantidad: number): string[] {
  const out: string[] = [];
  for (let i = cantidad - 1; i >= 0; i--) out.push(sumarMeses(hasta, -i));
  return out;
}

/** Período PRECARGADO del form "Registrar chequeo" (reemplaza a
 *  proximoPeriodoChequeo, superseded por la regla de día D — resolución #1
 *  del panel adversarial 2026-07-11): deuda simple, un chequeo salda —
 *  si el último chequeado quedó atrás del habilitado, precarga el
 *  habilitado; si ya está al día (o adelantada), precarga el siguiente al
 *  último (que va a aparecer marcado "índices aún no publicados" en el
 *  select de opcionesPeriodo). */
export function periodoPrecargado(r: { chequeos: RdtChequeo[] }): string {
  const habilitado = periodoHabilitado();
  const uc = ultimoChequeo(r);
  if (!uc) return habilitado;
  if (uc.periodo < habilitado) return habilitado;
  return periodoSiguiente(uc.periodo);
}

/** Opciones del select de período del form de chequeo: desde el siguiente
 *  al último chequeado (o habilitado-3 si la RDT no chequeó nunca, backfill
 *  acotado) hasta periodoSiguiente(habilitado) inclusive, tope 14 opciones.
 *  Los períodos > habilitado van marcados noPublicado=true — el día D
 *  RECLAMA, no PROHÍBE (resolución #1): se pueden elegir igual. */
export function opcionesPeriodo(r: { chequeos: RdtChequeo[] }): Array<{ periodo: string; noPublicado: boolean }> {
  const habilitado = periodoHabilitado();
  const uc = ultimoChequeo(r);
  const desde = uc ? periodoSiguiente(uc.periodo) : sumarMeses(habilitado, -3);
  const hasta = periodoSiguiente(habilitado);

  const opciones: Array<{ periodo: string; noPublicado: boolean }> = [];
  let cursor = desde;
  while (opciones.length < 14) {
    opciones.push({ periodo: cursor, noPublicado: cursor > habilitado });
    if (cursor >= hasta) break; // cubrió el tope (o una RDT adelantada ya lo pasó)
    cursor = periodoSiguiente(cursor);
  }
  return opciones;
}

/** Códigos de hito del catálogo (`hitos-catalogo.ts`) por clave de step del
 *  stepper — la entrega se resuelve aparte (es XOR, dos códigos posibles). */
export const STEP_KEY_TO_HITO_CODIGO: Record<string, string> = {
  aprobado: 'aprobado',
  poliza: 'poliza_ec_enviada',
  facturado: 'facturado',
};

// ---------------------------------------------------------------------------
// R1 (Juan, 2026-09-14): "la entrega exige salto previo" — la entrega es el
// primer paso POSTERIOR al salto/cálculo, así que sin chequeo que saltó
// (Provisoria) o sin «Monto informado» (Definitiva) no hay nada que
// entregar. Espejo manual de motivoEntregaSinSalto (backend/src/routes/rdts.ts,
// cerca de derivarEstadoActual) — MISMOS textos, palabra por palabra, porque
// este aviso de UI y el 422 del server tienen que decir lo mismo. Medido en
// staging (2026-09-14): 0 migradas con Aprobado/Facturado sin salto ni monto
// → el backfill de entregas del histórico no se ve afectado por esta guarda.
// ---------------------------------------------------------------------------

/** Mismo string que ESTADO_A_CALCULAR del backend (backend/src/domain/rdt-estado.ts)
 *  — no se importa cross-workspace (frontend/backend son paquetes npm
 *  separados), se copia el literal, igual que el resto de este archivo, que
 *  ya es un espejo manual de reglas del backend (ver periodoHabilitado()). */
const ESTADO_A_CALCULAR = 'A calcular';

/** R1 (Juan, 2026-09-14): motivo por el que todavía no se puede registrar la
 *  entrega — espeja motivoEntregaSinSalto del backend. null = habilitada.
 *  `salto`/`estado` van PLANOS (no anidados bajo un `estado: {...}`
 *  compuesto): así es como RdtEstadoDerivado los expone en la raíz de
 *  RdtDetalle/RdtResumen (`rdt.salto`, `rdt.estado`), spreadeados por la API. */
export function motivoEntregaBloqueada(rdt: { tipo: TipoRdt; salto: unknown; estado: string }): string | null {
  if (rdt.tipo === 'Provisoria' && rdt.salto === null) {
    return 'Antes registrá el chequeo que saltó — la entrega viene después del salto';
  }
  if (rdt.tipo === 'Definitiva' && rdt.estado === ESTADO_A_CALCULAR) {
    return 'Antes informá el monto calculado — la entrega viene después del cálculo';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Orden de la lista de RDTs de la obra: período (del salto, del monto
// vigente en Definitivas, o de la entrega) descendente, sin período arriba
// de todo; empate por fecha de creación.
// ---------------------------------------------------------------------------

function sortKey(r: RdtResumen): string {
  // Definitivas: el período de gestión es el del monto vigente (rdt.montos
  // viene last-wins, el primero es el vigente) — antes que el fallback de
  // Provisoria, que no aplica (una Definitiva no tiene `salto`).
  if (r.tipo === 'Definitiva' && r.montos[0]?.periodo) return r.montos[0].periodo;
  if (r.salto) return r.salto.periodo;
  const ec = hitoAplicable(r.hitos, 'entregado_cliente');
  if (ec?.fecha) return ec.fecha.slice(0, 7);
  return '9999';
}

export function ordenarRdts(rdts: RdtResumen[]): RdtResumen[] {
  return [...rdts].sort((a, b) => {
    const ka = sortKey(a);
    const kb = sortKey(b);
    if (ka !== kb) return ka < kb ? 1 : -1;
    if (a.creadoAt !== b.creadoAt) return a.creadoAt < b.creadoAt ? 1 : -1;
    // Empate real (migradas bulk-insertadas al mismo segundo, el backend
    // tampoco desempata): orden determinístico por id en vez de dejar el
    // comparador sin devolver 0 nunca (viola el contrato de sort → orden
    // inestable entre refetches). Id más alto = creada después → primero,
    // consistente con el resto del criterio "más nuevo arriba".
    return b.id - a.id;
  });
}

export function esPendiente(r: RdtResumen): boolean {
  return !r.finalizada && !r.archivada;
}

// ---------------------------------------------------------------------------
// Procedencia de los datos proyectados en la lápida ("todo es por
// actividad" — ningún dato de gestión se carga por formulario de ficha,
// todo entra por un paso del hilo; la lápida lo refleja con quién/cuándo).
// ---------------------------------------------------------------------------

/** Procedencia "vivo" (paso real) — `periodo` solo lo llenan monto/pct.
 *  `claveHilo` es la clave del ítem del hilo al que scrollear (ancla
 *  `act-${claveHilo}` en actividad.tsx) — puntos 8/9/14 v0.9.0. */
export interface ProcedenciaPaso {
  origen: 'paso';
  autor: string;
  fecha: string;
  periodo?: string;
  claveHilo: string;
}
export interface ProcedenciaHistorico {
  origen: 'historico';
  claveHilo: null;
}
export type Procedencia = ProcedenciaPaso | ProcedenciaHistorico | null;

/** Procedencia del MONTO de una Definitiva: el paso "Monto informado"
 *  vigente primero; sin pasos y con `montoHistorico` → histórico (migrada
 *  sin re-carga todavía); null si no hay nada cargado. */
export function procedenciaMonto(rdt: { montos: RdtMonto[]; montoHistorico: number | null }): Procedencia {
  const vigente = rdt.montos.find(m => m.vigente);
  if (vigente) {
    return {
      origen: 'paso',
      autor: vigente.autor?.nombre ?? 'Histórico',
      fecha: vigente.fecha,
      periodo: vigente.periodo,
      claveHilo: `monto-${vigente.id}`,
    };
  }
  if (rdt.montoHistorico != null) return { origen: 'historico', claveHilo: null };
  return null;
}

/** Procedencia de la REFERENCIA/expediente (ambos tipos): mismo criterio,
 *  sin período (el paso "Expediente" no tiene fecha de negocio propia, gana
 *  por creado_at/id — la `fecha` acá es el creadoAt del paso). */
export function procedenciaReferencia(rdt: { referencias: RdtReferencia[]; referenciaHistorico: string | null }): Procedencia {
  const vigente = rdt.referencias.find(r => r.vigente);
  if (vigente) {
    return { origen: 'paso', autor: vigente.autor?.nombre ?? 'Histórico', fecha: vigente.creadoAt, claveHilo: `referencia-${vigente.id}` };
  }
  if (rdt.referenciaHistorico != null) return { origen: 'historico', claveHilo: null };
  return null;
}

/** Procedencia del % de una Provisoria: sale del CHEQUEO QUE SALTÓ
 *  (valorPct vive ahí, no como campo suelto) — fallback al indicePct legacy
 *  (migradas sin el chequeo de salto todavía normalizado). */
export function procedenciaPct(rdt: { chequeos: RdtChequeo[]; indicePct: number | null }): Procedencia {
  const saltoChq = rdt.chequeos.find(c => c.salta);
  if (saltoChq) {
    return {
      origen: 'paso',
      autor: saltoChq.autor?.nombre ?? 'Histórico',
      fecha: saltoChq.creadoAt,
      periodo: saltoChq.periodo,
      claveHilo: `chequeo-${saltoChq.id}`,
    };
  }
  if (rdt.indicePct != null) return { origen: 'historico', claveHilo: null };
  return null;
}

// ---------------------------------------------------------------------------
// Ciclo de vida (stepper) POR TIPO:
//   Provisoria: Saltó → Entrega (XOR) → Aprobado → [Póliza] → Facturado.
//   Definitiva: Calculado → Entrega (XOR) → Aprobado → [Póliza] → Facturado
//   ("lo mismo, pero con montos" — Juan 2026-07-11: Calculado es el espejo
//   de Saltó, done con ≥1 monto vivo o montoHistorico).
// ---------------------------------------------------------------------------

export type StepColor = 'salto' | 'entrega' | 'aprobado' | 'poliza' | 'facturado';

export interface LifecycleStep {
  key: string;
  nombre: string;
  done: boolean;
  /** Todavía no se eligió la rama de entrega (obra/cliente) — se muestra en itálica. */
  fork: boolean;
  fecha: string | null;
  sub: string | null;
  forkSub: string | null;
  color: StepColor;
  /** Autor del paso que completó este step — para el tooltip del stepper
   *  (punto 5 v0.9.0). Null si no está hecho o es un hueco histórico sin
   *  fila real (saltoHistorico/calculadoHistorico/entregaHistorica). */
  autor: string | null;
  /** Nota del paso, si tiene (punto 5 v0.9.0) — mismo criterio null que `autor`. */
  nota: string | null;
  /** Clave del ítem del hilo al que scrollear con el stepper clickeable
   *  (puntos 8/9/14 v0.9.0): `hito-${codigo}` para steps que son un hito,
   *  `chequeo-${id}` para el salto, `monto-${id}` para el calculado — NULL
   *  en steps «histórico» de migradas sin fila real que apuntar. */
  claveHilo: string | null;
  /** Marca «(editado)» del stepper (BACKLOG «Hitos editados sin marca
   *  (editado)», Juan #15): la fecha de este step (un hito — entrega/
   *  aprobado/póliza/facturado) se corrigió vía "Corregir hitos". `false`
   *  en salto/calculado (chequeo/monto ya llevan su propia marca en el
   *  hilo, no en el stepper) y en steps sin fila real que apuntar. */
  editado: boolean;
}

export interface LifecycleRdt {
  tipo: TipoRdt;
  hitos: RdtHitoDetalle[];
  chequeos: RdtChequeo[];
  salto: { periodo: string; valorPct: number | null; fechaIndices: string | null } | null;
  montos: RdtMonto[];
  montoHistorico: number | null;
  migrada: boolean;
}

/** Autoría es-AR de un step del ciclo — mismo criterio que
 *  nombreAutorOpcional() de más abajo, pero devolviendo null (no '—') cuando
 *  no hay ninguna procedencia: acá null significa "no mostrar autor" en el
 *  tooltip del stepper (punto 5), no un placeholder visual. */
function autorDeStep(migrada: boolean, completadoPor: { nombre: string } | null): string | null {
  if (completadoPor) return completadoPor.nombre;
  return migrada ? AUTOR_HISTORICO : null;
}

export function lifecycle(rdt: LifecycleRdt): LifecycleStep[] {
  const entregaElegidaHito = entregaElegida(rdt.hitos);
  const entregaCompleta = entregaCompletada(rdt.hitos);
  const aprobado = hitoAplicable(rdt.hitos, 'aprobado');
  const poliza = hitoAplicable(rdt.hitos, 'poliza_ec_enviada');
  const facturado = hitoAplicable(rdt.hitos, 'facturado');
  // Migradas con hitos hechos pero sin chequeo/monto registrado: el
  // salto/cálculo ocurrió (el legacy lo probaba con la fecha de índices)
  // pero no está como paso todavía → se marca hecho "histórico", no
  // pendiente (mismo criterio preview, ahora compartido por los 2 tipos).
  const primerPasoHistorico = rdt.migrada && rdt.hitos.some(h => h.fecha);
  // Mismo criterio para la Entrega: en las migradas la entrega nunca se
  // registró (la PowerApp no tenía ese campo), así que si ya está Aprobada
  // (o Facturada) la entrega es un hueco histórico, no un paso accionable —
  // sin este tratamiento proximoPaso() (que solo mira `done`) marca "Entrega"
  // como próximo paso en vez de la póliza/el facturado. Decisión de Juan
  // (25/9/2026): «Solo para las redes MIGRADAS que tienen aprobación podemos
  // saltear el paso de entrega» — espejo de faltaEntregaPrevia() del backend
  // (backend/src/domain/rdt-estado.ts), que deja completar póliza/facturado
  // sin entrega en ESE caso. Una migrada sin aprobado, o una rede creada en
  // la app, sigue teniendo la entrega como paso.
  const entregaHistorica = rdt.migrada && !entregaElegidaHito && Boolean(aprobado?.fecha || facturado?.fecha);
  // El chequeo que hizo saltar (mismo criterio que procedenciaPct()) — fuente
  // de autor/nota/claveHilo del step "Saltó" (punto 5/8/9 v0.9.0).
  const saltoChq = rdt.chequeos.find(c => c.salta) ?? null;

  const steps: LifecycleStep[] = [];

  if (rdt.tipo === 'Definitiva') {
    const montoVigente = rdt.montos.find(m => m.vigente) ?? null;
    const calculadoHecho = Boolean(montoVigente) || rdt.montoHistorico != null;
    const calculadoHistorico = !calculadoHecho && primerPasoHistorico;

    let sub: string | null = null;
    if (montoVigente) {
      // Partes presentes (espejo exacto del ítem «Monto informado» del
      // hilo, actividad.tsx ~L293-295): monto y % son los dos opcionales
      // (Definitivas de SOFSE/ADIF por FRI/%, Juan QA 2026-09-14) — antes
      // el stepper decía "sin monto" con un vigente que solo traía %
      // (hallazgo 4 de la ronda QA 2026-09-15, ej. 3,87 %).
      const montoTxt =
        [montoVigente.monto != null ? fmtMonto.format(montoVigente.monto) : null, montoVigente.valorPct != null ? `${fmtPct.format(montoVigente.valorPct)} %` : null]
          .filter((p): p is string => p !== null)
          .join(' · ') || 'sin monto';
      sub = `${montoTxt} · per. ${perLabel(montoVigente.periodo)}`;
    } else if (rdt.montoHistorico != null) {
      sub = `${fmtMonto.format(rdt.montoHistorico)} · histórico`;
    } else if (calculadoHistorico) {
      sub = 'histórico';
    }

    steps.push({
      key: 'calculado',
      nombre: 'Calculado',
      done: calculadoHecho || calculadoHistorico,
      fork: false,
      fecha: montoVigente?.fecha ?? null,
      sub,
      forkSub: null,
      // Mismo slot semántico que "salto" (espejo de diseño Saltó↔Calculado):
      // reusa STEP_COLOR_CLASS/CONN_DONE_CLASS de ciclo-vida.tsx sin sumar
      // una key nueva a StepColor.
      color: 'salto',
      autor: montoVigente ? (montoVigente.autor?.nombre ?? 'Histórico') : null,
      nota: montoVigente?.nota ?? null,
      // NULL en calculadoHistorico (sin fila real que apuntar) — sale solo:
      // montoVigente es undefined en esa rama.
      claveHilo: montoVigente ? `monto-${montoVigente.id}` : null,
      // Sin marca acá: un monto corregido ya la lleva en el hilo (RdtMonto
      // .editadoAt, patrón previo a esta spec) — el stepper de "calculado"
      // no la duplica.
      editado: false,
    });
  } else {
    const s = rdt.salto;
    const saltoHistorico = !s && primerPasoHistorico;
    steps.push({
      key: 'salto',
      nombre: 'Saltó',
      done: Boolean(s) || saltoHistorico,
      fork: false,
      fecha: s ? s.fechaIndices : null,
      sub: s
        ? `per. ${perLabel(s.periodo)}${s.valorPct != null ? ` · ${fmtPct.format(s.valorPct)} %` : ''}`
        : saltoHistorico
          ? 'histórico'
          : null,
      forkSub: null,
      color: 'salto',
      autor: saltoChq ? (saltoChq.autor?.nombre ?? 'Histórico') : null,
      nota: saltoChq?.nota ?? null,
      // NULL en saltoHistorico (sin chequeo real, migrada con fecha de
      // índices legacy) — sale solo: saltoChq es null en esa rama.
      claveHilo: saltoChq ? `chequeo-${saltoChq.id}` : null,
      // Sin marca acá: un chequeo corregido ya la lleva en el hilo
      // (RdtChequeo.editadoAt, migración 024) — el stepper de "salto" no
      // la duplica.
      editado: false,
    });
  }

  steps.push({
    key: 'entrega',
    nombre: entregaElegidaHito ? entregaElegidaHito.nombre : 'Entrega',
    done: Boolean(entregaCompleta) || entregaHistorica,
    // Fork (itálica) mientras no se eligió ninguna rama todavía — una vez
    // elegida (aunque esté pendiente de fecha) ya se muestra su nombre real.
    // Una migrada aprobada/facturada sin entrega tampoco es "fork": no hay
    // nada para elegir, es un hueco cerrado del histórico (mismo trato que el
    // primer paso).
    fork: !entregaElegidaHito && !entregaHistorica,
    fecha: entregaCompleta ? entregaCompleta.fecha : null,
    sub: entregaHistorica ? 'no registrada (histórico)' : null,
    forkSub: entregaElegidaHito || entregaHistorica ? null : 'a obra ó al cliente',
    color: 'entrega',
    autor: entregaCompleta ? autorDeStep(rdt.migrada, entregaCompleta.completadoPor) : null,
    nota: entregaCompleta?.nota ?? null,
    // NULL en entregaHistorica (migrada aprobada sin fila de entrega real)
    // — sale solo: entregaCompleta es null en esa rama.
    claveHilo: entregaCompleta ? `hito-${entregaCompleta.codigo}` : null,
    // Marca «(editado)» (BACKLOG Juan #15): fecha corregida vía "Corregir
    // hitos" — sub-texto del stepper, ciclo-vida.tsx.
    editado: Boolean(entregaCompleta?.editadoAt),
  });

  steps.push({
    key: 'aprobado',
    nombre: 'Aprobado',
    done: Boolean(aprobado?.fecha),
    fork: false,
    fecha: aprobado?.fecha ?? null,
    sub: null,
    forkSub: null,
    color: 'aprobado',
    autor: aprobado?.fecha ? autorDeStep(rdt.migrada, aprobado.completadoPor) : null,
    nota: aprobado?.fecha ? (aprobado.nota ?? null) : null,
    claveHilo: aprobado?.fecha ? `hito-${aprobado.codigo}` : null,
    editado: aprobado?.fecha ? Boolean(aprobado.editadoAt) : false,
  });

  if (poliza) {
    steps.push({
      key: 'poliza',
      nombre: 'Póliza EC enviada',
      done: Boolean(poliza.fecha),
      fork: false,
      fecha: poliza.fecha,
      sub: null,
      forkSub: null,
      color: 'poliza',
      autor: poliza.fecha ? autorDeStep(rdt.migrada, poliza.completadoPor) : null,
      nota: poliza.fecha ? (poliza.nota ?? null) : null,
      claveHilo: poliza.fecha ? `hito-${poliza.codigo}` : null,
      editado: poliza.fecha ? Boolean(poliza.editadoAt) : false,
    });
  }

  steps.push({
    key: 'facturado',
    nombre: 'Facturado',
    done: Boolean(facturado?.fecha),
    fork: false,
    fecha: facturado?.fecha ?? null,
    sub: null,
    forkSub: null,
    color: 'facturado',
    autor: facturado?.fecha ? autorDeStep(rdt.migrada, facturado.completadoPor) : null,
    nota: facturado?.fecha ? (facturado.nota ?? null) : null,
    claveHilo: facturado?.fecha ? `hito-${facturado.codigo}` : null,
    editado: facturado?.fecha ? Boolean(facturado.editadoAt) : false,
  });

  return steps;
}

/** Clave del próximo paso accionable (primer step no-hecho), o null si el
 *  ciclo ya está completo. El knob punteado de ese step abre su form en Actividad. */
export function proximoPaso(steps: LifecycleStep[]): string | null {
  return steps.find(st => !st.done)?.key ?? null;
}

// ---------------------------------------------------------------------------
// Actividad: hilo único mezclando pasos tipados (montos, referencias,
// chequeos, hitos, eventos de identidad, seguimientos, adjuntos), la
// creación y los comentarios — orden descendente por fecha/hora.
// ---------------------------------------------------------------------------

export type ActividadItem =
  | {
      t: 'comentario';
      ts: string;
      id: number;
      autorId: number | null;
      autor: string;
      fotoUrl: string | null;
      texto: string | null;
      editado: boolean;
      borrado: boolean;
      borradoPor: string | null;
    }
  | {
      t: 'hito';
      ts: string;
      autor: string;
      fotoUrl: string | null;
      nombre: string;
      fecha: string;
      codigo: string;
      nota: string | null;
      // Marca «(editado)» (BACKLOG «Hitos editados sin marca (editado)»,
      // Juan #15): la fecha de este hito se corrigió vía "Corregir hitos"
      // — mismo criterio que chequeo/monto/referencia (editadoAt !== null).
      editado: boolean;
    }
  | {
      t: 'chequeo';
      ts: string;
      autor: string;
      fotoUrl: string | null;
      id: number;
      periodo: string;
      valorPct: number | null;
      salta: boolean;
      fechaIndices: string | null;
      nota: string | null;
    }
  | {
      t: 'monto';
      ts: string;
      autor: string;
      fotoUrl: string | null;
      id: number;
      monto: number | null;
      valorPct: number | null;
      periodo: string;
      fecha: string;
      nota: string | null;
      editado: boolean;
      vigente: boolean;
    }
  | {
      t: 'referencia';
      ts: string;
      autor: string;
      fotoUrl: string | null;
      id: number;
      referencia: string;
      nota: string | null;
      editado: boolean;
      vigente: boolean;
    }
  | {
      t: 'evento';
      ts: string;
      autor: string;
      fotoUrl: string | null;
      id: number;
      campo: CampoEvento;
      antes: string | null;
      despues: string | null;
      nota: string | null;
    }
  | {
      t: 'creacion';
      ts: string;
      /** Fecha REAL de alta — SIEMPRE `rdt.creadoAt` (para migradas, el
       *  "Created" del legacy importado, ver
       *  backend/scripts/import-snapshot.ts:333 — no es un timestamp de
       *  corrida de migración, es la fecha real del registro viejo).
       *  Separada de `ts` a propósito: en una migrada `ts` es un SENTINEL
       *  de ORDEN ('0000-01-01 00:00:00', ver el builder más abajo), no
       *  una fecha real, y nunca debe mostrarse — actividad.tsx muestra
       *  `fechaAlta`, no `ts` (fix feedback Juan 2026-08-28: la rede
       *  1130/28 mostraba «01/01/00 00:00» en vez de la fecha de alta
       *  real, 12/04/21). */
      fechaAlta: string;
      autor: string;
      fotoUrl: string | null;
    }
  | {
      t: 'contacto';
      ts: string;
      autor: string;
      fotoUrl: string | null;
      id: number;
      interlocutor: Interlocutor;
      resultado: ResultadoSeguimiento;
      // Fecha REAL del contacto (elegida en el form, puede ser retroactiva) —
      // distinta de `ts` (que sigue siendo creadoAt: el orden del hilo NO
      // cambia, solo se muestra esta fecha en el item).
      fecha: string;
      nota: string | null;
    }
  | {
      t: 'adjunto';
      ts: string;
      autor: string;
      fotoUrl: string | null;
      id: number;
      nombre: string;
      tamanoBytes: number;
      tipo: TipoAdjunto;
      nota: string | null;
    };

/** Literal de autoría histórica — compartido con actividad.tsx (compara
 *  `item.autor === AUTOR_HISTORICO` para decidir si suma el texto visible
 *  junto al avatar, fix feedback Juan 2026-08-28) en vez de repetir el
 *  string suelto en dos archivos. */
export const AUTOR_HISTORICO = 'Histórico';

function nombreAutorComentario(c: ComentarioRdt): string {
  return c.autor?.nombre ?? c.autorHistorico ?? AUTOR_HISTORICO;
}

/** Autoría es-AR de un ítem del hilo con autor OPCIONAL (fila migrada sin
 *  autoría real: `autor`/`completadoPor` null): AUTOR_HISTORICO si la RDT
 *  es migrada, '—' defensivo si no (no debería pasar — un paso sin
 *  migrada con autor null es un bug de datos, no un caso de negocio
 *  esperado). Generaliza lo que antes era SOLO nombreAutorHito() (feedback
 *  Juan 2026-08-28, obra 1130 rede #28: el builder de chequeo usaba '—'
 *  fijo en vez de este criterio, dejaba el avatar con guion en vez de
 *  "H") — la reusan TODOS los builders de actividadItems() con autor
 *  nullable: hito, chequeo, evento, contacto (seguimiento), adjunto. NO
 *  monto/referencia/comentario, que ya resuelven AUTOR_HISTORICO
 *  incondicional (sin el gate de `migrada`) porque ahí un autor null es
 *  SIEMPRE histórico por contrato del backend — no hay ningún caso de '—'
 *  que corregir en esos tres. */
function nombreAutorOpcional(migrada: boolean, autor: { nombre: string } | null): string {
  return autor?.nombre ?? (migrada ? AUTOR_HISTORICO : '—');
}

export function tamanoLegible(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

const TIPO_ADJUNTO_LABEL: Record<Adjunto['tipo'], string> = {
  acta: 'Acta',
  resolucion: 'Resolución',
  otro: 'Otro',
};

export function tipoAdjuntoLabel(tipo: Adjunto['tipo']): string {
  return TIPO_ADJUNTO_LABEL[tipo] ?? tipo;
}

const INTERLOCUTOR_LABEL: Record<Seguimiento['interlocutor'], string> = {
  obra: 'obra',
  cliente: 'cliente',
  organismo: 'organismo',
  otro: 'otro',
};

export function interlocutorLabel(v: Seguimiento['interlocutor']): string {
  return INTERLOCUTOR_LABEL[v] ?? v;
}

const CAMPO_EVENTO_LABEL: Record<CampoEvento, string> = {
  nro: 'Número',
  responsable: 'Responsable',
  archivada: 'Archivado',
  hito: 'Paso',
  tipo: 'Tipo',
  chequeo: 'Chequeo', // por completitud — chequeo lleva redacción propia en actividad.tsx, igual que hito/tipo.
  monto: 'Monto informado', // ídem — corrección de monto lleva redacción propia (mismo patrón que chequeo).
  referencia: 'Expediente', // ídem — corrección de expediente (v0.14.0), redacción propia en actividad.tsx.
  posponer: 'Posposición', // ídem — el eco del Avivador (migración 044) lleva redacción propia en actividad.tsx.
};

/** Label es-AR del campo de un eco de identidad (rdt_eventos) — para
 *  componer "Número modificado: a → b" en el hilo (actividad.tsx). `hito`,
 *  `tipo` (v0.9.0) y `chequeo` (ecos de corrección, Juan QA 2026-09-14)
 *  llevan redacción propia en actividad.tsx (no siguen el patrón genérico
 *  "a → b") — esta label queda como fallback/referencia. */
export function eventoCampoLabel(campo: CampoEvento): string {
  return CAMPO_EVENTO_LABEL[campo] ?? campo;
}

/** Formatea es-AR el VALOR crudo de un eco de corrección (`rdt_eventos.campo`
 *  en `'chequeo'` o `'monto'`, ecos de corrección Juan QA 2026-09-14 —
 *  primero para chequeos migrados, generalizado en 0.11.0 para el paso
 *  «Monto informado»: «toda corrección deja eco en el hilo»). El backend
 *  manda `antes`/`despues` como "<etiqueta> · <valor crudo>" (mismo
 *  separador « · » que el eco de hito) — acá se formatea SOLO el valor; la
 *  etiqueta (período/%/índices/monto/fecha) ya viene en es-AR literal, se
 *  muestra tal cual. `crudo === '—'` (null del backend) se devuelve sin
 *  tocar. Antes se llamaba `eventoChequeoValor` — generalizado porque el
 *  mismo formato "<etiqueta> · <crudo>" ahora lo usan los dos campos. */
export function eventoValor(etiqueta: string, crudo: string): string {
  if (crudo === '—') return '—';
  if (etiqueta === 'período') return perLabel(crudo);
  if (etiqueta === '%') {
    const n = Number(crudo);
    return Number.isFinite(n) ? `${fmtPct.format(n)} %` : crudo;
  }
  if (etiqueta === 'índices' || etiqueta === 'fecha') return fFecha(crudo) ?? '—';
  if (etiqueta === 'monto') {
    const n = Number(crudo);
    return Number.isFinite(n) ? fmtMonto.format(n) : crudo;
  }
  // Eco de nota («dar eco a todo», Juan QA ronda 2 2026-09-15): el crudo es
  // el texto libre de `rdt_chequeos.nota` / `rdt_montos.nota` tal cual, solo
  // se le suman comillas angulares es-AR para distinguirlo en el hilo.
  if (etiqueta === 'nota') return `«${crudo}»`;
  return crudo;
}

export function actividadItems(rdt: RdtDetalle): ActividadItem[] {
  const items: ActividadItem[] = [];

  for (const c of rdt.comentarios) {
    items.push({
      t: 'comentario',
      ts: c.creadoAt,
      id: c.id,
      autorId: c.autor?.id ?? null,
      autor: nombreAutorComentario(c),
      fotoUrl: c.autor?.fotoUrl ?? null,
      texto: c.texto,
      editado: c.editadoAt !== null,
      borrado: c.borrado,
      borradoPor: c.borradoPor?.nombre ?? null,
    });
  }
  for (const h of rdt.hitos) {
    if (!h.fecha) continue;
    items.push({
      // Orden del hilo por CUÁNDO se cargó (completadoAt, patrón contactos)
      // — no por la fecha de negocio del hito (`fecha`, que puede ser
      // retroactiva). Fallback a la fecha de negocio SOLO en migradas, donde
      // completado_at es NULL a propósito (punto 13 v0.9.0). El cuerpo del
      // ítem sigue mostrando `fecha` (ver actividad.tsx).
      t: 'hito',
      ts: h.completadoAt ?? `${h.fecha.slice(0, 10)} 00:00:00`,
      autor: nombreAutorOpcional(rdt.migrada, h.completadoPor),
      // Foto del usuario que completó el hito (QA en vivo Juan 2026-09-14,
      // obra 1426: el hito mostraba iniciales «JC» mientras el ítem de al
      // lado — mismo autor — mostraba la foto real). Migradas sin
      // `completadoPor` siguen cayendo en null -> fallback a iniciales.
      fotoUrl: h.completadoPor?.fotoUrl ?? null,
      nombre: h.nombre,
      fecha: h.fecha,
      codigo: h.codigo,
      nota: h.nota,
      editado: h.editadoAt !== null,
    });
  }
  for (const c of rdt.chequeos) {
    items.push({
      t: 'chequeo',
      ts: c.creadoAt,
      autor: nombreAutorOpcional(rdt.migrada, c.autor),
      fotoUrl: c.autor?.fotoUrl ?? null,
      id: c.id,
      periodo: c.periodo,
      valorPct: c.valorPct,
      salta: c.salta,
      fechaIndices: c.fechaIndices,
      nota: c.nota,
    });
  }
  for (const m of rdt.montos) {
    items.push({
      t: 'monto',
      ts: m.creadoAt,
      autor: m.autor?.nombre ?? AUTOR_HISTORICO,
      fotoUrl: m.autor?.fotoUrl ?? null,
      id: m.id,
      monto: m.monto,
      valorPct: m.valorPct,
      periodo: m.periodo,
      fecha: m.fecha,
      nota: m.nota,
      editado: m.editadoAt !== null,
      vigente: m.vigente,
    });
  }
  for (const rf of rdt.referencias) {
    items.push({
      t: 'referencia',
      ts: rf.creadoAt,
      autor: rf.autor?.nombre ?? AUTOR_HISTORICO,
      fotoUrl: rf.autor?.fotoUrl ?? null,
      id: rf.id,
      referencia: rf.referencia,
      nota: rf.nota,
      editado: rf.editadoAt !== null,
      vigente: rf.vigente,
    });
  }
  for (const ev of rdt.eventos) {
    items.push({
      t: 'evento',
      ts: ev.creadoAt,
      autor: nombreAutorOpcional(rdt.migrada, ev.autor),
      fotoUrl: ev.autor?.fotoUrl ?? null,
      id: ev.id,
      campo: ev.campo,
      antes: ev.antes,
      despues: ev.despues,
      nota: ev.nota,
    });
  }
  // Creación: paso de sistema "Rede #N creada". Migradas: SIEMPRE al fondo
  // del hilo (2/3 tienen actividad real anterior al Created del legacy —
  // recarga masiva 2021, no inventar cronología) — se logra con un ts
  // mínimo, que el sort descendente manda al final sin lógica especial.
  // `fechaAlta` abajo lleva la fecha real (rdt.creadoAt siempre, nunca el
  // sentinel) — es la que actividad.tsx efectivamente muestra.
  items.push({
    t: 'creacion',
    ts: rdt.migrada ? '0000-01-01 00:00:00' : rdt.creadoAt,
    fechaAlta: rdt.creadoAt,
    autor: rdt.creadoPor?.nombre ?? AUTOR_HISTORICO,
    fotoUrl: rdt.creadoPor?.fotoUrl ?? null,
  });
  for (const s of rdt.seguimientos) {
    items.push({
      t: 'contacto',
      ts: s.creadoAt,
      autor: nombreAutorOpcional(rdt.migrada, s.autor),
      fotoUrl: s.autor?.fotoUrl ?? null,
      id: s.id,
      interlocutor: s.interlocutor,
      resultado: s.resultado,
      fecha: s.fecha,
      nota: s.nota,
    });
  }
  for (const a of rdt.adjuntos) {
    items.push({
      t: 'adjunto',
      ts: a.creadoAt,
      autor: nombreAutorOpcional(rdt.migrada, a.autor),
      fotoUrl: a.autor?.fotoUrl ?? null,
      id: a.id,
      nombre: a.nombre,
      tamanoBytes: a.tamanoBytes,
      tipo: a.tipo,
      nota: a.nota,
    });
  }

  // Catálogo de `orden` por código de hito — desempate de hitos migrados que
  // comparten fecha (punto 13 v0.9.0): el más avanzado del ciclo va primero.
  // Vive en un Map local (no en el ActividadItem, que ya expone `codigo`
  // suficiente para el resto del contrato) para no ensuciar el tipo público.
  const ordenPorCodigoHito = new Map(rdt.hitos.map(h => [h.codigo, h.orden]));

  return items.sort((a, b) => {
    if (a.ts !== b.ts) return a.ts < b.ts ? 1 : -1;
    // Empates reales (p.ej. hitos migrados comparten "fecha 00:00:00", o
    // chequeos/comentarios creados el mismo segundo) — desempate determinístico
    // en vez de -1 siempre, que viola el contrato de sort y reordena el hilo
    // arbitrariamente entre refetches. Prioridad (punto 13): 1) id numérico
    // DESC cuando ambos ítems lo tienen (antes comparaba claves como texto:
    // 'monto-10' < 'monto-9' lexicográficamente, invertía el orden real);
    // 2) hitos empatados sin id → `orden` del catálogo DESC; 3) itemKey como
    // último recurso (nunca deja de devolver un orden determinístico).
    const ida = idNumerico(a);
    const idb = idNumerico(b);
    if (ida !== null && idb !== null) {
      if (ida !== idb) return idb - ida;
    } else if (a.t === 'hito' && b.t === 'hito') {
      const oa = ordenPorCodigoHito.get(a.codigo) ?? 0;
      const ob = ordenPorCodigoHito.get(b.codigo) ?? 0;
      if (oa !== ob) return ob - oa;
    }
    const ka = itemKey(a);
    const kb = itemKey(b);
    if (ka === kb) return 0;
    return ka < kb ? 1 : -1;
  });
}

/** El `id` numérico propio de un ítem, o null para los que no lo tienen
 *  (hito se ancla por código, creación no tiene fila) — usado SOLO para el
 *  desempate del sort de arriba. */
function idNumerico(it: ActividadItem): number | null {
  return it.t === 'hito' || it.t === 'creacion' ? null : it.id;
}

/** Clave estable de un ítem de Actividad — la usan tanto el desempate del
 *  sort de arriba como la ancla del DOM (`id="act-${itemKey(item)}"` en
 *  actividad.tsx) y el `key` de React de cada fila: los ítems sin id
 *  numérico propio (hito, creación) arman una clave a mano. El hito se
 *  ancla por CÓDIGO (no por fecha+nombre, que cambian con una edición) —
 *  robustece el scroll del stepper clickeable (punto 9 v0.9.0). */
export function itemKey(it: ActividadItem): string {
  if (it.t === 'hito') return `hito-${it.codigo}`;
  if (it.t === 'creacion') return 'creacion';
  return `${it.t}-${it.id}`;
}

// ---------------------------------------------------------------------------
// Catálogo de pasos de la paleta "+ Paso" (buscador con sinónimos, patrón
// Power Query) — cada entrada declara sus palabras clave de búsqueda y los
// tipos de RDT en los que aplica (filtrada por pasosParaTipo()).
// ---------------------------------------------------------------------------

export type TipoPaso = 'chequeo' | 'hito' | 'contacto' | 'adjunto' | 'comentario' | 'expediente' | 'monto';

export interface PasoDef {
  grupo: string;
  key: TipoPaso;
  label: string;
  kw: string;
  tipos: TipoRdt[];
}

export const PASOS: PasoDef[] = [
  { grupo: 'Gestión', key: 'chequeo', label: 'Chequeo de índices', kw: 'indices salto salta gatillo periodo', tipos: ['Provisoria'] },
  {
    grupo: 'Gestión',
    key: 'monto',
    label: 'Monto informado',
    kw: 'monto importe calculo definitivo plata valor',
    tipos: ['Definitiva'],
  },
  {
    grupo: 'Gestión',
    key: 'expediente',
    label: 'Expediente',
    kw: 'referencia expediente numero ex nota',
    tipos: ['Provisoria', 'Definitiva'],
  },
  {
    grupo: 'Gestión',
    key: 'hito',
    label: 'Completar hito',
    kw: 'entrega entregar obra cliente aprobado poliza facturar facturado',
    tipos: ['Provisoria', 'Definitiva'],
  },
  {
    grupo: 'Seguimiento',
    key: 'contacto',
    label: 'Contacto (obra, cliente, organismo…)',
    kw: 'llamada reclamo seguimiento pregunta respuesta',
    tipos: ['Provisoria', 'Definitiva'],
  },
  {
    grupo: 'Seguimiento',
    key: 'adjunto',
    label: 'Adjuntar archivo',
    kw: 'acta archivo documento pdf nota',
    tipos: ['Provisoria', 'Definitiva'],
  },
  {
    grupo: 'Seguimiento',
    key: 'comentario',
    label: 'Comentario',
    kw: 'nota texto novedad',
    tipos: ['Provisoria', 'Definitiva'],
  },
];

/** Subconjunto de PASOS aplicable a un tipo de RDT — guardas por tipo
 *  construibles ya (resolución #9 del panel adversarial): "Chequeo de
 *  índices" solo Provisorias, "Monto informado" solo Definitivas. */
export function pasosParaTipo(tipo: TipoRdt): PasoDef[] {
  return PASOS.filter(p => p.tipos.includes(tipo));
}

// Normaliza sin acentos/mayúsculas para el filtro de la paleta (mismo
// criterio que lib/client-color.ts normText, copia local para no acoplar
// este módulo de dominio a un helper pensado para Obras).
const COMBINING_DIACRITICS_START = 0x0300;
const COMBINING_DIACRITICS_END = 0x036f;
export function normText(s: string): string {
  const decomposed = s.toLowerCase().normalize('NFD');
  let out = '';
  for (const ch of decomposed) {
    const code = ch.codePointAt(0) ?? 0;
    if (code < COMBINING_DIACRITICS_START || code > COMBINING_DIACRITICS_END) out += ch;
  }
  return out;
}
